/**
 * Card payment inside the chat for paid appointment types (AGENTS.md 11.3). Stripe.js is loaded
 * from js.stripe.com only at this step and initialised for the business's connected account
 * (direct charge, no platform fee). Confirmation comes from the webhook, polled by the store.
 * Verified against https://docs.stripe.com/connect/direct-charges on 12 October 2026.
 */
import { useEffect, useRef, useState } from "preact/hooks";
import { t, type Locale } from "@wapadesk/i18n";
import type { PaymentState } from "../store";

interface StripeLike {
  elements(opts: { clientSecret: string; appearance?: unknown }): {
    create(type: "payment", opts?: unknown): { mount(el: HTMLElement): void; unmount(): void };
  };
  confirmPayment(opts: {
    elements: unknown;
    redirect: "if_required";
    confirmParams: { return_url: string };
  }): Promise<{ error?: { message?: string }; paymentIntent?: { status: string } }>;
}
declare global {
  interface Window {
    Stripe?: (key: string, opts?: { stripeAccount?: string }) => StripeLike;
  }
}

let loading: Promise<void> | undefined;
function loadStripeJs(): Promise<void> {
  if (window.Stripe) return Promise.resolve();
  loading ??= new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://js.stripe.com/v3/";
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("stripe_js_failed"));
    document.head.appendChild(s);
  });
  return loading;
}

export function Payment({
  locale,
  payment,
  onConfirmed,
  onFailed,
  onCancel,
  title,
  note,
}: {
  /** Deposit pay page (17 October 2026): its own heading and note. */
  title?: string | undefined;
  note?: string | undefined;
  locale: Locale;
  payment: PaymentState;
  onConfirmed: () => Promise<void>;
  onFailed: () => void | Promise<void>;
  onCancel: () => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const stripeRef = useRef<StripeLike | undefined>(undefined);
  const elementsRef = useRef<ReturnType<StripeLike["elements"]> | undefined>(undefined);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | undefined>();
  const amount = (() => {
    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: payment.currency,
      }).format(payment.amount / 100);
    } catch {
      return `${payment.currency} ${(payment.amount / 100).toFixed(2)}`;
    }
  })();

  useEffect(() => {
    let cancelled = false;
    let element: { mount(el: HTMLElement): void; unmount(): void } | undefined;
    loadStripeJs()
      .then(() => {
        if (cancelled || !box.current || !window.Stripe) return;
        const stripe = window.Stripe(payment.publishableKey, {
          stripeAccount: payment.stripeAccountId,
        });
        stripeRef.current = stripe;
        const elements = stripe.elements({
          clientSecret: payment.clientSecret,
          appearance: { theme: "stripe", variables: { borderRadius: "10px" } },
        });
        elementsRef.current = elements;
        element = elements.create("payment", { layout: "tabs" });
        element.mount(box.current);
        setReady(true);
      })
      .catch(() => setMessage(t(locale, "booking.unavailable")));
    return () => {
      cancelled = true;
      element?.unmount();
    };
  }, [payment.clientSecret, payment.publishableKey, payment.stripeAccountId, locale]);

  const pay = async () => {
    if (!stripeRef.current || !elementsRef.current) return;
    setBusy(true);
    setMessage(undefined);
    const r = await stripeRef.current.confirmPayment({
      elements: elementsRef.current,
      redirect: "if_required",
      confirmParams: { return_url: location.href },
    });
    if (r.error) {
      setMessage(r.error.message ?? t(locale, "booking.payFailed"));
      setBusy(false);
      void onFailed();
      return;
    }
    await onConfirmed();
    setBusy(false);
  };

  return (
    <div class="in booking pay" role="group" aria-label={t(locale, "booking.payTitle")}>
      <p class="sub">
        <strong>{title ?? t(locale, "booking.payTitle")}</strong> · {amount}
      </p>
      <p class="small muted">{note ?? t(locale, "booking.payNote")}</p>
      <div ref={box} class="pay-box" />
      {payment.phase === "confirming" && <p class="small">{t(locale, "booking.paying")}</p>}
      {payment.phase === "expired" && <p class="err">{t(locale, "booking.payExpired")}</p>}
      {message && <p class="err">{message}</p>}
      <div class="row">
        <button type="button" class="link" disabled={busy} onClick={onCancel}>
          {t(locale, "booking.back")}
        </button>
        <button
          type="button"
          class="primary"
          disabled={!ready || busy || payment.phase === "confirming"}
          onClick={() => void pay()}
        >
          {t(locale, "booking.payButton")} {amount}
        </button>
      </div>
    </div>
  );
}
